import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { trackEvent } from "../../lib/analytics";
import { LeadForm } from "./LeadForm";
import { LiveRecap } from "./LiveRecap";

const Html = ({ as: Tag = "span", html, className }) => (
  <Tag className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

const Block = ({ b, slug }) => {
  switch (b.t) {
    case "h2":
      return (
        <h2 id={b.id} className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mt-14 mb-5 scroll-mt-24">
          {b.text}
        </h2>
      );
    case "h3":
      return <h3 className="font-head font-bold uppercase text-xl mt-9 mb-3">{b.text}</h3>;
    case "p":
      return <Html as="p" className="text-zinc-300 leading-relaxed mb-5" html={b.html} />;
    case "ul":
      return (
        <ul className="space-y-2.5 mb-6">
          {b.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-zinc-300 leading-relaxed">
              <span className="text-volt font-mono2 shrink-0">›</span>
              <Html html={it} />
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-3 mb-6">
          {b.items.map((it, i) => (
            <li key={i} className="flex gap-4 text-zinc-300 leading-relaxed">
              <span className="font-head font-black text-volt text-xl leading-none shrink-0 w-6">{i + 1}</span>
              <Html html={it} />
            </li>
          ))}
        </ol>
      );
    case "table":
      return (
        <div className="mb-2">
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="bg-[#0c0c0c]">
                  {b.head.map((h, i) => (
                    <th key={i} className="text-left px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-normal whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, ri) => (
                  <tr key={ri} className="border-t border-white/5">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 ${ci === 0 ? "text-white font-semibold" : "text-zinc-300"}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {b.note && <p className="font-mono2 text-[10px] uppercase tracking-wider text-zinc-600 mt-2 mb-6">{b.note}</p>}
          {!b.note && <div className="mb-6" />}
        </div>
      );
    case "quote":
      return (
        <blockquote className="border-l-2 border-volt pl-5 my-9">
          <Html as="p" className="text-white text-lg leading-relaxed italic mb-2" html={b.html} />
          <cite className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 not-italic">— {b.by}</cite>
        </blockquote>
      );
    case "disclosure":
      return (
        <p className="flex items-start gap-2.5 border border-white/10 bg-[#0c0c0c] p-4 text-zinc-500 text-xs leading-relaxed mb-6" data-testid="article-affiliate-disclosure">
          <Info size={14} className="shrink-0 mt-0.5 text-volt" />
          <span>
            Gear note: equipment links below are affiliate links. If you buy through them, Hyrox Ninja
            earns a commission at no extra cost to you.
          </span>
        </p>
      );
    case "callout":
      return (
        <aside className="border border-volt/40 bg-volt/5 p-6 sm:p-7 my-8">
          {b.title && <p className="eyebrow mb-2">{b.title}</p>}
          <Html as="p" className="text-zinc-300 leading-relaxed" html={b.html} />
        </aside>
      );
    case "lead":
      return <LeadForm headline={b.headline} source={slug} />;
    case "live-recap":
      return <LiveRecap />;
    default:
      return null;
  }
};

export const ArticleRenderer = ({ blocks, slug }) => {
  const navigate = useNavigate();
  const onClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if ((a.getAttribute("rel") || "").includes("sponsored")) {
      trackEvent("affiliate_click", { partner: "centr", placement: `blog_${slug}` });
    }
    if (href.startsWith("/")) {
      e.preventDefault();
      navigate(href);
    }
  };
  /* intercept internal links for SPA navigation */
  return (
    <div className="article-body" onClick={onClick} data-testid="article-body">
      {blocks.map((b, i) => (
        <Block key={i} b={b} slug={slug} />
      ))}
    </div>
  );
};
